import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, errText } from "@/context/AuthContext";
import { Zap, ArrowRight } from "lucide-react";

const DEMO = [
  { role: "Admin", email: "admin@startuptimes.com", pw: "admin123" },
  { role: "Lead-Gen", email: "leadgen@startuptimes.com", pw: "leadgen123" },
  { role: "BDE", email: "bde@startuptimes.com", pw: "bde123" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@startuptimes.com");
  const [password, setPassword] = useState("admin123");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (e2) {
      setErr(errText(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090B]">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg"
          alt="office"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/40" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="h-10 w-10 bg-gold text-black grid place-items-center hard-shadow mb-6">
            <Zap strokeWidth={2.5} />
          </div>
          <h1 className="font-head text-5xl font-extrabold tracking-tighter leading-none">
            Sell to the<br />right founders.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-md text-sm leading-relaxed">
            A CRM that learns your Ideal Customer Profile from every won client, scores each
            lead against it, and tells your team exactly what to do next.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-8 w-8 bg-gold text-black grid place-items-center"><Zap className="h-4 w-4" /></div>
            <span className="text-white font-head font-bold">SALES IQ</span>
          </div>
          <h2 className="text-white font-head text-2xl font-bold mb-1">Welcome back</h2>
          <p className="text-zinc-500 text-sm mb-6 font-mono">Startup Times · Sales Intelligence</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1 block">Email</label>
              <input
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 text-sm focus:border-klein focus:outline-none focus:ring-1 focus:ring-klein"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1 block">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2.5 text-sm focus:border-klein focus:outline-none focus:ring-1 focus:ring-klein"
              />
            </div>
            {err && <div data-testid="login-error" className="text-red-400 text-xs font-mono">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full bg-klein hover:bg-klein-hover text-white font-medium py-2.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"} <ArrowRight className="h-4 w-4" />
            </button>
          </form>

          <div className="mt-8 border-t border-zinc-800 pt-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2 font-mono">Demo accounts — click to fill</p>
            <div className="space-y-1.5">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  data-testid={`demo-${d.role.toLowerCase()}`}
                  onClick={() => { setEmail(d.email); setPassword(d.pw); }}
                  className="w-full text-left flex justify-between items-center bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 px-3 py-2 transition-colors"
                >
                  <span className="text-xs text-zinc-300 font-mono">{d.email}</span>
                  <span className="text-[10px] text-gold uppercase">{d.role}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
